// src/utils/solar.js
// Pure solar-geometry helpers (no React, no THREE). Shared by the
// ClimateAnalysisPanel (readouts) and sunPathScene (sun vector / rays).
//
// Convention used by the scene: +X = East, +Z = North, +Y = up,
// azimuth measured clockwise from North (N=0, E=90, S=180, W=270).

export const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
// representative day-of-year for the 21st of each month
export const DOY21 = [21, 52, 80, 111, 141, 172, 202, 233, 264, 294, 325, 355];

// ---------------------------------------------------------------------------
// NOAA solar position -> altitude & azimuth in degrees
// ---------------------------------------------------------------------------
export function solarPosition(latDeg, lngDeg, tzHours, dayOfYear, minutesOfDay) {
  const rad = Math.PI / 180, deg = 180 / Math.PI;
  const lat = latDeg * rad;
  const g = (2 * Math.PI / 365) * ((dayOfYear - 1) + (minutesOfDay / 60 - 12) / 24);
  const eqtime = 229.18 * (0.000075 + 0.001868 * Math.cos(g) - 0.032077 * Math.sin(g)
    - 0.014615 * Math.cos(2 * g) - 0.040849 * Math.sin(2 * g));
  const decl = 0.006918 - 0.399912 * Math.cos(g) + 0.070257 * Math.sin(g)
    - 0.006758 * Math.cos(2 * g) + 0.000907 * Math.sin(2 * g)
    - 0.002697 * Math.cos(3 * g) + 0.00148 * Math.sin(3 * g); // radians
  const timeOffset = eqtime + 4 * lngDeg - 60 * tzHours;       // minutes
  const tst = minutesOfDay + timeOffset;                        // true solar time (min)
  const ha = (tst / 4) - 180;                                   // hour angle (deg)
  const haR = ha * rad;
  let cosZen = Math.sin(lat) * Math.sin(decl) + Math.cos(lat) * Math.cos(decl) * Math.cos(haR);
  cosZen = Math.min(1, Math.max(-1, cosZen));
  const zen = Math.acos(cosZen);
  const altitude = 90 - zen * deg;
  let cosAz = (Math.sin(decl) - Math.sin(lat) * Math.cos(zen)) / (Math.cos(lat) * Math.sin(zen) || 1e-9);
  cosAz = Math.min(1, Math.max(-1, cosAz));
  let azimuth = Math.acos(cosAz) * deg;   // 0..180 from North
  if (ha > 0) azimuth = 360 - azimuth;    // afternoon -> west
  return { altitude, azimuth };
}

// unit direction vector pointing TOWARD the sun (plain object; scene converts to THREE.Vector3)
export function sunVector(altDeg, aziDeg) {
  const rad = Math.PI / 180, a = altDeg * rad, z = aziDeg * rad, ca = Math.cos(a);
  return { x: Math.sin(z) * ca, y: Math.sin(a), z: Math.cos(z) * ca };
}

// sunrise / sunset minutes (altitude crossing 0)
export function sunTimes(lat, lng, tz, doy) {
  let rise = null, set = null, prev = null;
  for (let m = 0; m <= 1440; m += 2) {
    const a = solarPosition(lat, lng, tz, doy, m).altitude;
    if (prev !== null) {
      if (prev < 0 && a >= 0) rise = m;
      if (prev >= 0 && a < 0) set = m;
    }
    prev = a;
  }
  return { rise, set };
}

export const fmtHM = (min) => {
  if (min == null) return '–';
  const t = ((Math.round(min) % 1440) + 1440) % 1440;
  const h = Math.floor(t / 60), m = Math.round(t % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const compass16 = (az) => {
  const d = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return d[Math.round(az / 22.5) % 16];
};

// exposure heat scale 0..1 -> hex int (deep blue -> teal -> green -> amber -> orange)
export function heatColor(t) {
  t = Math.min(1, Math.max(0, t));
  const stops = [[0x1b,0x3b,0x6f],[0x1f,0x8a,0x8f],[0x4f,0xc4,0x6b],[0xff,0xd2,0x4d],[0xff,0x8a,0x2b]];
  const x = t * (stops.length - 1), i = Math.floor(x), f = x - i;
  const a = stops[i], b = stops[Math.min(i + 1, stops.length - 1)];
  const c = (k) => Math.round(a[k] + (b[k] - a[k]) * f);
  return (c(0) << 16) | (c(1) << 8) | c(2);
}
export const heatCss = (t) => '#' + heatColor(t).toString(16).padStart(6, '0');
